/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Close } from '@mui/icons-material';
import interact from 'interactjs';

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
    z-index: 2400;
    pointer-events: none;
`;

const ModalContent = styled.div`
    background: rgba(255, 255, 255, 0.96);
    border-radius: 18px;
    padding: 24px;
    width: 420px;
    max-width: calc(100vw - 24px);
    position: relative;
    box-shadow: 0 24px 60px rgba(4, 28, 63, 0.18);
    border: 1px solid rgba(0, 39, 85, 0.1);
    pointer-events: auto;
`;

const DragHandle = styled.div`
    width: 100%;
    height: 18px;
    margin-bottom: 8px;
    cursor: move;
    border-radius: 12px;
    // background: linear-gradient(90deg, rgba(0, 39, 85, 0.18), rgba(0, 39, 85, 0.05));
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    width: 40px;
    height: 40px;
    background: none;
    border: none;
    cursor: pointer;
    color: #888;
    transition: color 0.2s ease-in-out;
    border-radius: 50%;

    &:hover {
        color: #333;
        background-color: rgba(0, 0, 0, 0.05);
    }
`;

const Section = styled.div`
    margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
    margin: 0 0 16px 0;
    font-size: 17px;
    font-weight: 700;
    color: #0f2d52;
`;

const RadioGroup = styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
`;

const RadioLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #0f2d52;
    font-weight: 600;
`;

const RadioInput = styled.input`
    width: 14px;
    height: 14px;
    accent-color: #007bff;
`;

const InputGroup = styled.div`
    margin-bottom: 16px;
`;

const Input = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    border: 1px solid #cfd6e3;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
    color: #0f2d52;
    background: #f8fbff;

    &:focus {
        outline: none;
        border-color: #1976d2;
        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.2);
    }
`;

const InfoText = styled.p`
    font-size: 13px;
    color: #5c6883;
    margin: 8px 0 0 0;
    line-height: 1.5;
    white-space: pre-line;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 28px;
`;

const ActionButton = styled.button`
    flex: 1;
    padding: 14px 24px;
    border: none;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
    }
`;

const InflowButton = styled(ActionButton)`
    background-color: #3cb371;
    color: white;

    &:hover {
        background-color: #2e8b57;
    }
`;

const OutflowButton = styled(ActionButton)`
    background-color: #ffa500;
    color: white;

    &:hover {
        background-color: #cc8400;
    }
`;

const MoveAnalysisModal = ({ isOpen, onClose, setMovPopParams, setLineLayerVisible }) => {
    const [type, setType] = useState('population');
    const [cnt, setCnt] = useState('5');
    const [houseType, setHouseType] = useState('0');

    useEffect(() => {
        const element = document.getElementById('move_analysis_modal');
        if (!element) return;

        interact(element).draggable({
            allowFrom: '.draggable-header',
            listeners: {
                move(event) {
                    const target = event.target;
                    const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
                    const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;
                    target.style.transform = `translate(${x}px, ${y}px)`;
                    target.setAttribute('data-x', x);
                    target.setAttribute('data-y', y);
                },
            },
        });

        return () => {
            interact(element).unset();
        };
    }, []);

    const onClickInFlowButton = () => {
        setMovPopParams({
            type: type,
            apiName: "getInfoRoute",
            cnt: cnt,
            direction: "in",
            houseType: houseType
        })

        onClose();
        setLineLayerVisible(true);
    }

    const onClickOutFlowButton = () => {
        setMovPopParams({
            type: type,
            apiName: "getInfoRouteHouseHold",
            cnt: cnt,
            direction: "out",
            houseType: houseType
        })

        onClose();
        setLineLayerVisible(true);
    }

    

    if (!isOpen) return null;

    

    return (
        <ModalOverlay>
            <ModalContent id="move_analysis_modal" data-x="0" data-y="0">
                <DragHandle className="draggable-header" />
                <CloseButton onClick={onClose}>
                    <Close />
                </CloseButton>

                <Section>
                    <SectionTitle>유형</SectionTitle>
                    <RadioGroup>
                        <RadioLabel>
                            <RadioInput
                                type="radio"
                                name="type"
                                value="population"
                                checked={type === 'population'}
                                onChange={(e) => setType(e.target.value)}
                            />
                            인구
                        </RadioLabel>
                        <RadioLabel>
                            <RadioInput
                                type="radio"
                                name="type"
                                value="household"
                                checked={type === 'household'}
                                onChange={(e) => setType(e.target.value)}
                            />
                            가구
                        </RadioLabel>
                    </RadioGroup>
                </Section>

                {type === 'household' && (
                    <Section>
                        <SectionTitle>가구유형</SectionTitle>
                        <RadioGroup>
                            <RadioLabel>
                                <RadioInput
                                    type="radio"
                                    name="householdType"
                                    value="0"
                                    checked={houseType === '0'}
                                    onChange={(e) => setHouseType(e.target.value)}
                                />
                                전체가구
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput
                                    type="radio"
                                    name="householdType"
                                    value="1"
                                    checked={houseType === '1'}
                                    onChange={(e) => setHouseType(e.target.value)}
                                />
                                1인가구
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput
                                    type="radio"
                                    name="householdType"
                                    value="2"
                                    checked={houseType === '2'}
                                    onChange={(e) => setHouseType(e.target.value)}
                                />
                                2인부부
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput
                                    type="radio"
                                    name="householdType"
                                    value="3"
                                    checked={houseType === '3'}
                                    onChange={(e) => setHouseType(e.target.value)}
                                />
                                부모+자녀
                            </RadioLabel>
                        </RadioGroup>
                    </Section>
                )}

                <Section>
                    <SectionTitle>전출입 지역 순위</SectionTitle>
                    <InputGroup>
                        <Input
                            type="number"
                            min="1"
                            max="10"
                            value={cnt}
                            onChange={(e) => {
                                const value = e.target.value;
                                setCnt(value);
                            }}
                            onBlur={(e) => {
                                const value = e.target.value;
                                const numValue = parseInt(value);

                                if (value === '' || isNaN(numValue) || numValue < 1) {
                                    setCnt('1');
                                } else if(numValue > 10) {
                                    setCnt('10');
                                }
                            }}
                            onFocus={(e) => e.target.select()}
                            placeholder="전출입 지역 순위를 입력해주세요."
                        />
                        <InfoText>
                            {type === 'population'
                                ? '1. 전출입 지역 순위에 5를 입력시 5순위 지역까지 조회(최대 10순위)'
                                : '1. 전출입 지역 순위에 5를 입력시 5순위 지역까지 조회(최대 10순위)\n2. 세대주가 포함된 가구 이동에 한함\n3. 연령대는 세대주 만나이 기준\n4. 가구 유형 분류 중 부모+자녀에는\n  - 세대주의 부모, 조부모, 손자녀가 포함된 경우는 제외하되, 형제자매, 기타친척이 포함된 경우\n  - 한부모(부 or 모) 가정도 포함'}
                        </InfoText>
                    </InputGroup>
                </Section>

                <ButtonGroup>
                    <InflowButton onClick={() => onClickInFlowButton()}>전입</InflowButton>
                    <OutflowButton onClick={() => onClickOutFlowButton()}>전출</OutflowButton>
                </ButtonGroup>
            </ModalContent>
        </ModalOverlay>
    );
};

export default MoveAnalysisModal;
